import {
  BOSS_DEFEAT_SCORE,
  BOSS_RETREAT_S,
  BOSS_SCREEN_X,
  BOSS_TRANSITION_S,
  DEFAULT_BOSS_HITBOX,
  DEFAULT_VERTICAL_MOTION,
  type BossDef,
} from '../../data/boss';
import { SIM_HZ, VIEW_WIDTH } from '../../data/tuning';
import { patternFor } from './patterns';
import { clearPhaseObjects, type PatternEnv } from './attacks';
import type { TickContext } from '../tick';

/**
 * Boss fight framework (GDD §9.1, issue #89). The state machine every boss shares:
 *
 *   entering > phase > transition > phase > ... > retreating > done
 *
 * - `entering`: the boss is not in the arena yet (the plane has not reached it): not damaging, no attacks.
 * - `phase`: its pattern attacks and a hit costs 1 HP. Crossing a phase threshold starts a 1.5 s `transition`,
 *   in which the boss is invulnerable, does not attack and the hits (decoys, #90) do nothing.
 * - the last HP starts the comic `retreating` (nobody dies, GDD §3.4): no hitbox, +5 000 points; when it ends
 *   the corridor to the US sky opens (`boss:retreat-finished` and `boss:corridor-open`).
 * - respawning resets the fight to full HP, phase 1 and its start position.
 * Pure: the runner moves it with the view and reads its body; time only enters through `step`.
 */
export type BossStatus = 'entering' | 'phase' | 'transition' | 'retreating' | 'done';

export const TRANSITION_TICKS = Math.round(BOSS_TRANSITION_S * SIM_HZ);
export const RETREAT_TICKS = Math.round(BOSS_RETREAT_S * SIM_HZ);

export interface BossFight {
  readonly def: BossDef;
  hp: number;
  status: BossStatus;
  /** Phase number, from 1. */
  phase: number;
  transitionLeft: number;
  retreatLeft: number;
  /** Ticks since the boss entered the arena: the vertical motion counts from them. */
  ticks: number;
  /** Position in the world, set by the runner from the view and the motion. */
  x: number;
  y: number;
  /** Calls of an attack pattern since the fight began; the transition never adds to it. */
  attacks: number;
}

export function createBossFight(def: BossDef): BossFight {
  const motion = def.verticalMotion ?? DEFAULT_VERTICAL_MOTION;
  return {
    def,
    hp: def.hp,
    status: 'entering',
    phase: 1,
    transitionLeft: 0,
    retreatLeft: 0,
    ticks: 0,
    x: 0,
    y: (motion.low + motion.high) / 2,
    attacks: 0,
  };
}

/** Back to full HP, phase 1 and the start of the fight (the respawn at CP-B, GDD §6.2 rule 5). */
export function resetBossFight(fight: BossFight): void {
  Object.assign(fight, createBossFight(fight.def));
}

/** Vertical position at a tick of the arena: the sine of the boss between its two heights. */
export function bossY(def: BossDef, ticks: number): number {
  const motion = def.verticalMotion ?? DEFAULT_VERTICAL_MOTION;
  const middle = (motion.low + motion.high) / 2;
  const amplitude = (motion.high - motion.low) / 2;
  return middle + amplitude * Math.sin((2 * Math.PI * ticks) / (motion.periodSeconds * SIM_HZ));
}

/** Screen position of the boss: 80 % of the visible width, from the left edge of the view. */
export const bossX = (viewLeft: number): number => viewLeft + BOSS_SCREEN_X * VIEW_WIDTH;

export const isDamaging = (fight: BossFight): boolean => fight.status === 'phase' || fight.status === 'transition';
export const isInvulnerable = (fight: BossFight): boolean => fight.status !== 'phase';

export const bossBody = (fight: BossFight) => ({
  x: fight.x,
  y: fight.y,
  width: (fight.def.hitbox ?? DEFAULT_BOSS_HITBOX).width,
  height: (fight.def.hitbox ?? DEFAULT_BOSS_HITBOX).height,
});

/** The phase for a number of HP: the last one whose threshold is reached. */
export function phaseFor(def: BossDef, hp: number): number {
  const index = def.phases.findIndex((phase) => hp >= phase.minHp);
  return index < 0 ? def.phases.length : index + 1;
}

export interface BossEvents {
  /** Events for the context, in order. */
  emit(event: { type: string; [detail: string]: unknown }): void;
}

/** The boss enters the arena (the plane reached it). */
export function enterArena(fight: BossFight, events: BossEvents): void {
  if (fight.status !== 'entering') return;
  fight.status = 'phase';
  events.emit({ type: 'boss:enter', bossId: fight.def.id });
}

/**
 * One hit (a decoy, #90): -1 HP unless the boss is not in a phase. Returns true when it counted. The last HP
 * starts the retreat; crossing a threshold starts the transition.
 */
export function hitBoss(fight: BossFight, events: BossEvents): boolean {
  if (fight.status !== 'phase') return false;
  fight.hp -= 1;
  events.emit({ type: 'boss:hit', bossId: fight.def.id, hp: fight.hp });
  if (fight.hp <= 0) {
    fight.hp = 0;
    fight.status = 'retreating';
    fight.retreatLeft = RETREAT_TICKS;
    events.emit({ type: 'boss:defeated', bossId: fight.def.id, points: BOSS_DEFEAT_SCORE });
    return true;
  }
  const next = phaseFor(fight.def, fight.hp);
  if (next !== fight.phase) {
    fight.status = 'transition';
    fight.transitionLeft = TRANSITION_TICKS;
    events.emit({ type: 'boss:transition-start', bossId: fight.def.id, phase: next });
  }
  return true;
}

/** One tick of the fight, after the runner has placed the boss. */
export function stepBossFight(fight: BossFight, context: TickContext, env?: PatternEnv): void {
  if (fight.status === 'entering' || fight.status === 'done') return;
  fight.ticks += 1;
  fight.y = bossY(fight.def, fight.ticks);
  if (fight.status === 'transition') {
    fight.transitionLeft -= 1;
    if (fight.transitionLeft <= 0) {
      fight.status = 'phase';
      fight.phase = phaseFor(fight.def, fight.hp);
      if (env) clearPhaseObjects(env.attacks);
      context.emit({ type: 'boss:transition-end', bossId: fight.def.id });
      context.emit({ type: 'boss:phase', bossId: fight.def.id, phase: fight.phase });
    }
    return;
  }
  if (fight.status === 'retreating') {
    fight.retreatLeft -= 1;
    if (fight.retreatLeft <= 0) {
      fight.status = 'done';
      context.emit({ type: 'boss:retreat-finished', bossId: fight.def.id });
      context.emit({ type: 'boss:corridor-open', bossId: fight.def.id });
    }
    return;
  }
  const pattern = patternFor(fight.def.phases[fight.phase - 1]?.pattern ?? '');
  if (pattern) {
    fight.attacks += 1;
    pattern(fight, context, env);
  }
}
